// Billing tab — current plan + subscription state, with links to the
// plan picker (/app/upgrade) and the Stripe Customer Portal (cancel /
// upgrade / downgrade / card / invoices, all Stripe-hosted).
//
// We never render card data or call Stripe from here: the portal is a
// one-time session link the backend mints, so the user lands straight
// in their own billing page without a Stripe login.

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ApiError, api } from "@/lib/api";
import { toast } from "@/stores/toastStore";
import { useDict, fmt } from "@/i18n/LocaleProvider";

interface SubscriptionState {
  plan_slug: string;
  plan_name: string;
  is_paid: boolean;
  stripe_status: string | null;
  billing_cycle: string | null;
  current_period_end: string | null;
  has_billing_account: boolean;
}

// Stripe status → label + colour. Only the states a customer can land in.
const STATUS_COLORS: Record<string, string> = {
  active: "rgb(var(--color-success))",
  trialing: "rgb(var(--color-success))",
  past_due: "rgb(var(--color-warning))",
  canceled: "rgb(var(--color-danger))",
  unpaid: "rgb(var(--color-danger))",
  incomplete: "rgb(var(--color-warning))",
};

function formatRenewal(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function BillingTab() {
  const t = useDict().app.billing;
  const navigate = useNavigate();
  const [openingPortal, setOpeningPortal] = useState(false);

  const sub = useQuery({
    queryKey: ["billing", "subscription"],
    queryFn: () => api<SubscriptionState>("/billing/subscription"),
  });

  async function openPortal() {
    setOpeningPortal(true);
    try {
      const { url } = await api<{ url: string }>("/billing/portal", {
        method: "POST",
      });
      window.location.assign(url);
    } catch (e) {
      const msg =
        e instanceof ApiError && typeof e.detail === "string"
          ? e.detail
          : t.portalError;
      toast.error(msg);
      setOpeningPortal(false);
    }
  }

  if (sub.isLoading) {
    return (
      <Card className="!p-6 text-center font-mono text-[12px] text-muted">
        {t.loading}
      </Card>
    );
  }

  const s = sub.data;
  if (!s) {
    return (
      <Card className="!p-6 text-center font-mono text-[12px] text-muted">
        {t.unavailable}
      </Card>
    );
  }

  const statusColor = s.stripe_status ? STATUS_COLORS[s.stripe_status] : null;
  const renewal = formatRenewal(s.current_period_end);
  const cycleLabel =
    s.billing_cycle === "yearly"
      ? t.cycles.yearly
      : s.billing_cycle === "monthly"
        ? t.cycles.monthly
        : null;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-1">
              {t.currentPlan}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-display text-[24px] font-bold text-content">
                {s.plan_name}
              </span>
              {statusColor && s.stripe_status && (
                <span
                  className="px-[6px] py-[2px] rounded-[3px] text-[9px] font-bold uppercase"
                  style={{
                    background: `${statusColor}22`,
                    border: `1px solid ${statusColor}88`,
                    color: statusColor,
                  }}
                >
                  {t.statuses[s.stripe_status as keyof typeof t.statuses] ?? s.stripe_status}
                </span>
              )}
            </div>

            <div className="font-mono text-[11px] text-muted mt-2 space-y-[2px]">
              {cycleLabel && <div>{t.billingLabel} {cycleLabel}</div>}
              {renewal && (
                <div>
                  {s.stripe_status === "canceled"
                    ? fmt(t.accessUntil, { date: renewal })
                    : fmt(t.renews, { date: renewal })}
                </div>
              )}
              {!s.is_paid && <div>{t.freeTier}</div>}
            </div>
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            <Button variant="primary" onClick={() => navigate("/app/upgrade")}>
              {s.is_paid ? t.changePlan : t.upgrade}
            </Button>
            {s.has_billing_account && (
              <Button
                variant="default"
                disabled={openingPortal}
                onClick={() => void openPortal()}
              >
                {openingPortal ? t.opening : t.manageBilling}
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="font-mono text-[10px] text-dim leading-[1.6]">
        {t.portalNote}
      </div>
    </div>
  );
}
